import React from 'react';
import { Skeleton, theme } from '@doveit/bricks';
import { Shooting } from '../../../providers/api/dtos';
import useShooting from '../../hooks/use-shooting/useShooting';
import UpdateShootingStatus, { UpdateShootingStatusProps } from '../update-shooting-status/UpdateShootingStatus';

export interface UpdateShootingStatusByIdProps extends Omit<UpdateShootingStatusProps, 'shooting'> {
  shootingId: NonNullable<Shooting['id']>,
}

/**
 * Loads the v2 shooting by id before rendering `UpdateShootingStatus`.
 * Useful when only the legacy shooting (e.g. from the SERP index) is available.
 */
const UpdateShootingStatusById: React.FC<UpdateShootingStatusByIdProps> = ({
  shootingId,
  size = 'S',
  onSuccess,
  ...rest
}) => {
  const {
    data: shooting,
    isLoading,
    error,
    mutate,
  } = useShooting(shootingId);

  const handleSuccess = React.useCallback((updatedShooting: Shooting) => {
    mutate(updatedShooting, { revalidate: false });
    onSuccess?.(updatedShooting);
  }, [mutate, onSuccess]);

  if (isLoading) {
    return <Skeleton width="100%" height={theme.remHeight[size]} css={{ maxWidth: '10rem' }} />;
  }

  if (error || !shooting) {
    return null;
  }

  return (
    <UpdateShootingStatus
      shooting={shooting}
      size={size}
      onSuccess={handleSuccess}
      {...rest}
    />
  );
};

export default UpdateShootingStatusById;
